'use client';

import { c as _c } from "react/compiler-runtime";
import { Button } from '@algoacademy/design-system/web/actions/Button';
import { Link } from '@algoacademy/design-system/web/actions/Link';
import { showErrorToastWithContactFormLink } from '@algoacademy/design-system/web/feedback/Toaster/ToasterError';
import { Checkbox, CheckboxField } from '@algoacademy/design-system/web/forms/Checkbox';
import { ErrorMessage, Field, Label } from '@algoacademy/design-system/web/forms/Fieldset';
import { Input } from '@algoacademy/design-system/web/forms/Input';
import { Textarea } from '@algoacademy/design-system/web/forms/Textarea';
import { VerticalSpacer } from '@algoacademy/design-system/web/layouts/VerticalSpacer';
import { FadeUp } from '@algoacademy/design-system/web/marketing/animation/FadeUp';
import { Fieldset } from '@headlessui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useTRPC } from '@/app/_trpc/config';
import type { ClientData } from '../../../page';
import { type CallFormSchema, callFormSchemaZ } from './schema';
type CallFormSectionProps = {
  activeStepIdx: number;
  setActiveStepIdx: (idx: number) => void;
  setClientData: (clientData: ClientData) => void;
};
export function CallFormSection(t0) {
  const $ = _c(80);
  const {
    activeStepIdx,
    setActiveStepIdx,
    setClientData
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      fullname: "",
      phoneNumber: "",
      message: "",
      acceptTerms: false
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      defaultValues: t1,
      mode: "onSubmit",
      resolver: zodResolver(callFormSchemaZ)
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const methods = useForm(t2);
  const {
    formState: t3
  } = methods;
  const {
    errors,
    isDirty
  } = t3;
  const trpc = useTRPC();
  let t4;
  if ($[2] !== trpc.phoneCallBooking.createOneTimePassword) {
    t4 = trpc.phoneCallBooking.createOneTimePassword.mutationOptions();
    $[2] = trpc.phoneCallBooking.createOneTimePassword;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const createOTPMutation = useMutation(t4);
  let t5;
  if ($[4] !== activeStepIdx || $[5] !== createOTPMutation || $[6] !== setActiveStepIdx || $[7] !== setClientData) {
    t5 = async data => {
      try {
        await createOTPMutation.mutateAsync({
          phoneNumber: data.phoneNumber
        });
        setClientData({
          fullname: data.fullname,
          phoneNumber: data.phoneNumber,
          message: data.message
        });
        toast.success("SMS wys\u0142any", {
          description: "Sprawd\u017A sw\xF3j telefon \u2013 kod weryfikacyjny jest ju\u017C w drodze."
        });
      } catch {
        showErrorToastWithContactFormLink();
        return;
      }
      setActiveStepIdx(activeStepIdx + 1);
    };
    $[4] = activeStepIdx;
    $[5] = createOTPMutation;
    $[6] = setActiveStepIdx;
    $[7] = setClientData;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const onSubmit = t5;
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <VerticalSpacer size="content" />;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== methods || $[11] !== onSubmit) {
    t7 = methods.handleSubmit(onSubmit);
    $[10] = methods;
    $[11] = onSubmit;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Label>Imię i nazwisko</Label>;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== methods) {
    t9 = methods.register("fullname");
    $[14] = methods;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t9) {
    t10 = <Input {...t9} name="fullname" type="text" placeholder={"Imi\u0119 i nazwisko"} />;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const t11 = errors.fullname?.message;
  let t12;
  if ($[18] !== t11) {
    t12 = <ErrorMessage>{t11}</ErrorMessage>;
    $[18] = t11;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== t10 || $[21] !== t12) {
    t13 = <Field>{t8}{t10}{t12}</Field>;
    $[20] = t10;
    $[21] = t12;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Label>Numer telefonu</Label>;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== methods) {
    t15 = methods.register("phoneNumber");
    $[24] = methods;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== t15) {
    t16 = <Input {...t15} name="phoneNumber" type="text" placeholder="+48-000-000-000" />;
    $[26] = t15;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  const t17 = errors.phoneNumber?.message;
  let t18;
  if ($[28] !== t17) {
    t18 = <ErrorMessage>{t17}</ErrorMessage>;
    $[28] = t17;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  let t19;
  if ($[30] !== t16 || $[31] !== t18) {
    t19 = <Field>{t14}{t16}{t18}</Field>;
    $[30] = t16;
    $[31] = t18;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  if ($[33] !== t13 || $[34] !== t19) {
    t20 = <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">{t13}{t19}</div>;
    $[33] = t13;
    $[34] = t19;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  let t21;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <Label>Wiadomość do mentora</Label>;
    $[36] = t21;
  } else {
    t21 = $[36];
  }
  let t22;
  if ($[37] !== methods) {
    t22 = methods.register("message");
    $[37] = methods;
    $[38] = t22;
  } else {
    t22 = $[38];
  }
  let t23;
  if ($[39] !== t22) {
    t23 = <Textarea {...t22} rows={6} name="message" placeholder={"Tematy lub pytania, kt\xF3re chcesz om\xF3wi\u0107 podczas rozmowy z mentorem."} className="min-h-10" />;
    $[39] = t22;
    $[40] = t23;
  } else {
    t23 = $[40];
  }
  const t24 = errors.message?.message;
  let t25;
  if ($[41] !== t24) {
    t25 = <ErrorMessage>{t24}</ErrorMessage>;
    $[41] = t24;
    $[42] = t25;
  } else {
    t25 = $[42];
  }
  let t26;
  if ($[43] !== t23 || $[44] !== t25) {
    t26 = <Field>{t21}{t23}{t25}</Field>;
    $[43] = t23;
    $[44] = t25;
    $[45] = t26;
  } else {
    t26 = $[45];
  }
  let t27;
  if ($[46] !== methods) {
    t27 = methods.register("acceptTerms");
    $[46] = methods;
    $[47] = t27;
  } else {
    t27 = $[47];
  }
  let t28;
  if ($[48] !== methods) {
    t28 = e => methods.setValue("acceptTerms", e);
    $[48] = methods;
    $[49] = t28;
  } else {
    t28 = $[49];
  }
  let t29;
  if ($[50] !== t27 || $[51] !== t28) {
    t29 = <Checkbox {...t27} onChange={t28} />;
    $[50] = t27;
    $[51] = t28;
    $[52] = t29;
  } else {
    t29 = $[52];
  }
  let t30;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <Label>Oświadczam, że akceptuję{" "}<Link href="/regulamin"><span className="font-semibold text-algoprimary">regulamin</span></Link></Label>;
    $[53] = t30;
  } else {
    t30 = $[53];
  }
  let t31;
  if ($[54] !== t29) {
    t31 = <CheckboxField>{t29}{t30}</CheckboxField>;
    $[54] = t29;
    $[55] = t31;
  } else {
    t31 = $[55];
  }
  const t32 = errors.acceptTerms?.message;
  let t33;
  if ($[56] !== t32) {
    t33 = <ErrorMessage>{t32}</ErrorMessage>;
    $[56] = t32;
    $[57] = t33;
  } else {
    t33 = $[57];
  }
  let t34;
  if ($[58] !== t31 || $[59] !== t33) {
    t34 = <Field>{t31}{t33}</Field>;
    $[58] = t31;
    $[59] = t33;
    $[60] = t34;
  } else {
    t34 = $[60];
  }
  let t35;
  if ($[61] !== t20 || $[62] !== t26 || $[63] !== t34) {
    t35 = <Fieldset className="space-y-2">{t20}{t26}{t34}</Fieldset>;
    $[61] = t20;
    $[62] = t26;
    $[63] = t34;
    $[64] = t35;
  } else {
    t35 = $[64];
  }
  let t36;
  if ($[65] !== activeStepIdx || $[66] !== setActiveStepIdx) {
    t36 = <Button outline={true} size="xl" onClick={() => setActiveStepIdx(activeStepIdx - 1)}>Powrót</Button>;
    $[65] = activeStepIdx;
    $[66] = setActiveStepIdx;
    $[67] = t36;
  } else {
    t36 = $[67];
  }
  const t37 = !isDirty;
  let t38;
  if ($[68] !== t37) {
    t38 = <Button size="xl" type="submit" disabled={t37}>Przejdź dalej</Button>;
    $[68] = t37;
    $[69] = t38;
  } else {
    t38 = $[69];
  }
  let t39;
  if ($[70] !== t36 || $[71] !== t38) {
    t39 = <div className="flex flex-row justify-end gap-3">{t36}{t38}</div>;
    $[70] = t36;
    $[71] = t38;
    $[72] = t39;
  } else {
    t39 = $[72];
  }
  let t40;
  if ($[73] !== t35 || $[74] !== t39 || $[75] !== t7) {
    t40 = <FadeUp><form onSubmit={t7} className="mx-auto max-w-5xl space-y-4">{t35}{t39}</form></FadeUp>;
    $[73] = t35;
    $[74] = t39;
    $[75] = t7;
    $[76] = t40;
  } else {
    t40 = $[76];
  }
  let t41;
  if ($[77] !== methods || $[78] !== t40) {
    t41 = <FormProvider {...methods}>{t6}{t40}</FormProvider>;
    $[77] = methods;
    $[78] = t40;
    $[79] = t41;
  } else {
    t41 = $[79];
  }
  return t41;
}